/**
 * TiktokOrdersService — the stored copy of the TikTok Shop order ledger, in
 * `TIKTOK_Orders` on the CRM site.
 *
 * IGCOM_Orders' twin, deliberately: same site, same default `sp` client, same
 * chunked append-only shape, same self-provisioning, same place on the backfill
 * allow-list. Where the two differ, tiktokOrdersStore.ts says why — and the
 * differences are all about WHAT is stored, never about how.
 *
 * WHY STORE IT AT ALL. The same reason as IG COM, only sharper. TikTok's order
 * search is a paged walk behind one spinner, and the window is whatever was
 * asked for; the questions Mikko wants answered — which months earn, which
 * SKUs move, which provinces buy — are questions about years. It also means
 * the screen opens on data instead of on a two-thousand-order walk, which is
 * the difference between a screen somebody checks daily and one they do not.
 *
 * NO BUYER NAME AND NO CONTACT DETAIL EVER REACHES THE LIST. TikTok masks
 * buyer details server-side for anyone off IGCOM_PII_USERS, so a stored name
 * would carry whichever version the person who pressed Sync was entitled to
 * see. `toStoredOrder` drops them at the boundary; the tests pin it.
 *
 * A sync writes ONLY orders the list does not already agree with
 * (changedOrders), so re-syncing an unchanged window costs one read and no
 * writes. TikTok exercises that harder than Shopify did — an order's status
 * walks from UNPAID to DELIVERED over days, so a recent window genuinely has
 * changed and must write, while the months behind it have not.
 */
import { sp } from "./spClient";
import { isListMissingError } from "./spErrors";
import { assertBackfillTarget } from "./backfillGuard";
import {
  TIKTOK_ORDERS_LIST, ORDERS_PER_ROW, pack, unpackChunk,
  type ITiktokStore, type ITiktokStoredOrder,
} from "./tiktokOrdersStore";

export * from "./tiktokOrdersStore";

/**
 * Every name here ships with the list spec below, so there is no site where
 * one column exists and another does not — the failure the `*_SELECT` /
 * `*_CORE` pair exists to survive (CLAUDE.md) cannot arise on a list created
 * whole.
 */
const TIKTOK_ORDERS_SELECT = "Id,rowsJson,count,fromDate,toDate,syncedBy,syncedAt";

export class TiktokOrdersService {
  private typeName?: string;

  private async type(): Promise<string> {
    if (this.typeName) return this.typeName;
    const r = await sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${TIKTOK_ORDERS_LIST}')?$select=ListItemEntityTypeFullName`);
    this.typeName = r.d.ListItemEntityTypeFullName;
    return this.typeName;
  }

  /**
   * Everything on the list, folded to one row per order, newest first.
   *
   * Oldest chunk applied first so a later sync overrides an earlier one — an
   * order that shipped this week replaces the AWAITING_SHIPMENT version of
   * itself rather than appearing twice.
   */
  public async readStore(): Promise<ITiktokStore> {
    let rows: Record<string, unknown>[];
    try {
      rows = await sp.getListItems<Record<string, unknown>>(
        TIKTOK_ORDERS_LIST, { select: TIKTOK_ORDERS_SELECT });
    } catch (e) {
      // Not provisioned yet is a STATE, not an error: the screen explains it
      // and offers to fix it. Anything else is a real failure and still throws.
      if (isListMissingError(e)) return { orders: [], syncedAt: "", syncedBy: "", missing: true };
      throw e;
    }

    const at = (r: Record<string, unknown>): string => String(r.syncedAt ?? "");
    const ordered = [...rows].sort(
      (a, b) => at(a).localeCompare(at(b)) || (a.Id as number) - (b.Id as number));

    const byId = new Map<string, ITiktokStoredOrder>();
    let syncedAt = "";
    let syncedBy = "";
    for (const r of ordered) {
      const stamp = at(r);
      if (stamp) { syncedAt = stamp; syncedBy = String(r.syncedBy ?? ""); }
      for (const o of unpackChunk(r.rowsJson)) byId.set(o.id, o);
    }

    const orders = [...byId.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return { orders, syncedAt, syncedBy, missing: false };
  }

  /** Write the orders a sync found, in chunks, reporting progress. */
  public async writeOrders(
    orders: readonly ITiktokStoredOrder[],
    syncedBy: string,
    onProgress?: (done: number, total: number) => void,
  ): Promise<number> {
    // Stated once, at the top, where it can be read: this list is on the
    // allow-list ON PURPOSE and the guard is what says so.
    assertBackfillTarget(TIKTOK_ORDERS_LIST);
    if (orders.length === 0) return 0;

    const type = await this.type();
    const syncedAt = new Date().toISOString();
    let done = 0;
    onProgress?.(0, orders.length);

    // Oldest first, so a chunk's fromDate/toDate describe a contiguous slice
    // and the list view reads like a timeline rather than a shuffle.
    const byDate = [...orders].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    for (let i = 0; i < byDate.length; i += ORDERS_PER_ROW) {
      const chunk = byDate.slice(i, i + ORDERS_PER_ROW);
      await sp.createItem(TIKTOK_ORDERS_LIST, {
        __metadata: { type },
        Title: `${syncedAt.slice(0, 10)} · ${chunk.length}`,
        rowsJson: JSON.stringify(chunk.map(pack)),
        count: chunk.length,
        fromDate: (chunk[0]?.createdAt ?? "").slice(0, 10),
        toDate: (chunk[chunk.length - 1]?.createdAt ?? "").slice(0, 10),
        syncedBy,
        syncedAt,
      });
      done += chunk.length;
      onProgress?.(done, byDate.length);
    }
    return done;
  }

  /**
   * Create the list on the CRM site. Explicit user action only, and only when
   * absent — the posture EmbroideryRequestService and IgcomOrdersService take.
   *
   * It is HERE rather than in the Migration Valve because the Valve provisions
   * the MANUFACTURING site's iSyn2_ lists, and this one belongs beside
   * IGCOM_Orders and B2B_Orders on the CRM site.
   */
  public async provision(onProgress?: (m: string) => void): Promise<void> {
    onProgress?.(`Creating ${TIKTOK_ORDERS_LIST}…`);
    await sp.raw(`/_api/web/lists`, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.List" },
        Title: TIKTOK_ORDERS_LIST,
        Description:
          "CRM · TikTok Shop — the stored copy of the marketplace order ledger, so trend analysis "
          + "does not re-walk TikTok's order pages on every screen load. HOLDS NO BUYER IDENTITY: "
          + "an order's facts, its city and province, its SKUs, and a buyer key only where TikTok "
          + "supplied a real one — never a name, an email, a phone or a street address. TikTok masks "
          + "buyer details per caller, so a stored name would differ by who synced it. Chunked — "
          + "rowsJson holds a few hundred orders, append-only, newest chunk wins per order. Read by "
          + "the TikTok Shop screen and by nothing else.",
        BaseTemplate: 100,
      }),
    });
    const fields: { name: string; kind: "text" | "note" | "number" | "datetime"; indexed?: boolean }[] = [
      { name: "rowsJson", kind: "note" },
      { name: "count", kind: "number" },
      { name: "fromDate", kind: "text" },
      { name: "toDate", kind: "text" },
      { name: "syncedBy", kind: "text" },
      { name: "syncedAt", kind: "datetime", indexed: true },
    ];
    const KIND = { text: 2, note: 3, datetime: 4, number: 9 };
    const url = `/_api/web/lists/getbytitle('${TIKTOK_ORDERS_LIST}')/fields`;
    for (const f of fields) {
      onProgress?.(`Adding ${f.name}…`);
      await sp.raw(url, {
        method: "POST",
        body: JSON.stringify({
          __metadata: { type: "SP.Field" },
          Title: f.name, FieldTypeKind: KIND[f.kind], Required: false,
        }),
      });
      // Indexed at creation, while the list is empty. SharePoint refuses to
      // index a list past the view threshold, and this one grows steadily.
      if (f.indexed) {
        await sp.raw(`${url}/getbytitle('${f.name}')`, {
          method: "POST",
          headers: { "X-HTTP-Method": "MERGE", "If-Match": "*" },
          body: JSON.stringify({ __metadata: { type: "SP.Field" }, Indexed: true }),
        });
      }
    }
    this.typeName = undefined;
    onProgress?.(`${TIKTOK_ORDERS_LIST} ready (${fields.length} columns)`);
  }
}

export const tiktokOrdersService = new TiktokOrdersService();
